import { AnimatePresence, MotionConfig, motion } from 'motion/react';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import useSVGSprite from '../../hooks/useSVGSprite';

const ImageSlider = ({ content, images, infinite = false }) => {
  const sprite = useSVGSprite();
  const [currentIndex, setCurrentIndex] = useState(infinite ? images.length : 0);
  const [isTransitioning, setIsTransitioning] = useState(true);

  const goToPrev = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex(prev => prev - 1);
  }, []);

  const goToNext = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex(prev => prev + 1);
  }, []);

  // Handle infinite carousel wrapping
  useEffect(() => {
    if (!infinite) return;

    const timer = setTimeout(() => {
      setIsTransitioning(false);
      if (currentIndex < images.length) {
        setCurrentIndex(2 * images.length - 1);
      } else if (currentIndex > 2 * images.length - 1) {
        setCurrentIndex(images.length);
      }
    }, 600); // Match animation duration

    return () => clearTimeout(timer);
  }, [currentIndex, images.length, infinite]);

  // Calculate which logical image (0 to images.length-1) should be highlighted
  const getActiveImageIndex = useCallback(() => {
    if (!infinite) {
      return currentIndex;
    }
    // Map any position back to the logical image index (0 to images.length-1)
    return ((currentIndex - images.length) % images.length + images.length) % images.length;
  }, [currentIndex, images.length, infinite]);

  const pointerStart = useRef(null);

  const onPointerDown = useCallback(e => {
    pointerStart.current = { x: e.clientX, y: e.clientY };
  }, []);

  const onPointerUp = useCallback(
    e => {
      if (!pointerStart.current) return;
      const dx = e.clientX - pointerStart.current.x;
      const dy = e.clientY - pointerStart.current.y;
      // Only count as swipe if horizontal movement exceeds vertical
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) goToNext();
        else goToPrev();
      }
      pointerStart.current = null;
    },
    [goToNext, goToPrev]
  );

  return (
    <>
      <div className="container">
        <div className="s-imageslider__header">
          {content && (
            <div className="s-imageslider__content">
              <div className="wysiwyg-block" dangerouslySetInnerHTML={{ __html: content }} />
            </div>
          )}
          <div className="s-imageslider__controls">
            <button
              type="button"
              className="s-imageslider__control s-imageslider__control--prev"
              disabled={!infinite && currentIndex <= 0}
              onClick={goToPrev}
              aria-label="Previous image"
            >
              <svg viewBox="0 0 50 24">
                <title>Arrow left</title>
                <use href={`${sprite}#arrow-left`} />
              </svg>
            </button>
            <button
              type="button"
              className="s-imageslider__control s-imageslider__control--next"
              disabled={!infinite && currentIndex >= images.length - 1}
              onClick={goToNext}
              aria-label="Next image"
            >
              <svg viewBox="0 0 50 24">
                <title>Arrow right</title>
                <use href={`${sprite}#arrow-right`} />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <MotionConfig reducedMotion="user">
        <div className="s-imageslider__slider">
          <motion.div
            className="s-imageslider__track"
            animate={{ x: `calc(-${currentIndex * 100}% - ${currentIndex} * var(--slider-gap))` }}
            transition={isTransitioning ? { duration: 0.6, ease: [0.645, 0.045, 0.355, 1] } : { duration: 0 }}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            style={{ touchAction: 'pan-y' }}
          >
            {infinite ? (
              <>
                {/* Clone images at the end */}
                {images.map((image, index) => (
                  <div
                    key={`clone-end-${index}`}
                    className={`s-imageslider__slide ${index === getActiveImageIndex() && currentIndex < images.length ? 's-imageslider__slide--active' : ''}`}
                  >
                    <figure className="s-imageslider__image">
                      <picture>
                        <source srcSet={image.sizes['2048x2048']} media="(min-width: 1024px)" />
                        <source srcSet={image.sizes['1536x1536']} media="(min-width: 768px)" />
                        <img src={image.sizes.large} alt={image.alt} draggable="false" />
                      </picture>
                    </figure>
                  </div>
                ))}
                {/* Original images */}
                {images.map((image, index) => (
                  <div
                    key={index}
                    className={`s-imageslider__slide ${index === getActiveImageIndex() && currentIndex >= images.length && currentIndex <= 2 * images.length - 1 ? 's-imageslider__slide--active' : ''}`}
                  >
                    <figure className="s-imageslider__image">
                      <picture>
                        <source srcSet={image.sizes['2048x2048']} media="(min-width: 1024px)" />
                        <source srcSet={image.sizes['1536x1536']} media="(min-width: 768px)" />
                        <img src={image.sizes.large} alt={image.alt} draggable="false" />
                      </picture>
                    </figure>
                  </div>
                ))}
                {/* Clone images at the beginning */}
                {images.map((image, index) => (
                  <div
                    key={`clone-start-${index}`}
                    className={`s-imageslider__slide ${index === getActiveImageIndex() && currentIndex > 2 * images.length - 1 ? 's-imageslider__slide--active' : ''}`}
                  >
                    <figure className="s-imageslider__image">
                      <picture>
                        <source srcSet={image.sizes['2048x2048']} media="(min-width: 1024px)" />
                        <source srcSet={image.sizes['1536x1536']} media="(min-width: 768px)" />
                        <img src={image.sizes.large} alt={image.alt} draggable="false" />
                      </picture>
                    </figure>
                  </div>
                ))}
              </>
            ) : (
              images.map((image, index) => (
                <div
                  key={index}
                  className={`s-imageslider__slide ${index === currentIndex ? 's-imageslider__slide--active' : ''}`}
                >
                  <figure className="s-imageslider__image">
                    <picture>
                      <source srcSet={image.sizes['2048x2048']} media="(min-width: 1024px)" />
                      <source srcSet={image.sizes['1536x1536']} media="(min-width: 768px)" />
                      <img src={image.sizes.large} alt={image.alt} draggable="false" />
                    </picture>
                  </figure>
                </div>
              ))
            )}
          </motion.div>
        </div>
      </MotionConfig>
    </>
  );
};

ImageSlider.propTypes = {
  content: PropTypes.string,
  images: PropTypes.array,
  infinite: PropTypes.bool
};

export default ImageSlider;
